import React, { useState } from 'react';
import { PS } from '../theme';
import { Icon } from '../lib/icons';
import { Avatar, CreditMeter } from '../lib/ui';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';
import { notifyError } from '../lib/toast';

function Stat({ label, value, tone }) {
  return (
    <div style={{ background: PS.panel, borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
      <div className="num" style={{ fontSize: 19, fontWeight: 600, color: tone || PS.text }}>{value}</div>
      <div style={{ fontSize: 10, color: PS.textMuted, marginTop: 2 }}>{label}</div>
    </div>
  );
}

// Sheet showing a person's reputation + a private concern form ("คนที่ควรรู้")
export default function PersonSheet({ circleId, person, onClose }) {
  const { user, promptLogin } = useAuth();
  const { data: rep } = useFetch(person?.user_id ? `/users/${person.user_id}/reputation` : null, [person?.user_id]);
  const [mode, setMode] = useState('info'); // info | concern
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!user) return promptLogin('แจ้งข้อสงสัยต้องเข้าสู่ระบบก่อน');
    if (!reason.trim()) return;
    setBusy(true);
    try {
      await api.post(`/circles/${circleId}/concern`, { subject_name: person.name, subject_user_id: person.user_id, reason: reason.trim() });
      setDone(true);
    } catch (e) {
      notifyError(e?.response?.data?.detail || 'เกิดข้อผิดพลาด');
    } finally { setBusy(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,18,16,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1600 }}>
      <div onClick={e => e.stopPropagation()} className="ps-fade-up" data-testid="person-sheet" style={{ width: '100%', maxWidth: 460, background: PS.surface2, borderRadius: '24px 24px 0 0', padding: '20px 20px max(20px, env(safe-area-inset-bottom))', fontFamily: PS.fontUI, maxHeight: '88vh', overflow: 'auto' }}>
        <div style={{ width: 40, height: 4, borderRadius: 2, background: PS.line, margin: '0 auto 16px' }} />

        {done ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ width: 58, height: 58, borderRadius: 29, background: 'oklch(0.88 0.08 80 / 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Icon.shield size={28} stroke="oklch(0.5 0.12 70)" /></div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>บันทึกข้อสงสัยแล้ว</div>
            <div style={{ fontSize: 12.5, color: PS.textMuted, marginTop: 8, lineHeight: 1.6 }}>เก็บเป็น<b style={{ color: PS.text }}>หลักฐานส่วนตัว</b>ของคุณ (คนอื่นในวงไม่เห็น) ลงนามแบบตรวจสอบได้ — จะถูกแนบในซองหลักฐานเมื่อคุณเปิดข้อพิพาท</div>
            <button data-testid="concern-done-btn" onClick={onClose} className="ps-press" style={{ width: '100%', height: 48, marginTop: 18, background: PS.ink, color: PS.page, border: 'none', borderRadius: 14, fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI }}>เข้าใจแล้ว</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
              <Avatar name={person.name} size={48} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{person.name}</div>
                <div style={{ fontSize: 11.5, color: PS.textMuted, marginTop: 2 }}>ประวัติบนเครือข่าย Peershare</div>
              </div>
              {rep && <CreditMeter score={rep.credit_score} size={56} />}
            </div>

            {mode === 'info' ? (
              <div className="ps-fade">
                {rep ? (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 10 }}>
                      <Stat label="ตรงเวลา" value={`${rep.on_time_pct}%`} tone={rep.on_time_pct >= 95 ? 'oklch(0.45 0.09 155)' : PS.text} />
                      <Stat label="อยู่ในวง" value={rep.circles_count} />
                      <Stat label="เป็นท้าว" value={rep.hosted_count} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                      <Stat label="งวดที่จ่าย" value={rep.total_paid_rounds} />
                      <Stat label="เคยถอนตัว" value={rep.exited_count} tone={rep.exited_count > 0 ? 'oklch(0.55 0.12 60)' : PS.text} />
                      <Stat label="เคยค้างจ่าย" value={rep.late_count} tone={rep.late_count > 0 ? PS.danger : PS.text} />
                    </div>
                    {(rep.exited_count > 0 || rep.late_count > 0) && (
                      <div style={{ marginTop: 12, padding: '10px 12px', background: 'oklch(0.88 0.08 80 / 0.4)', borderRadius: 12, fontSize: 11.5, color: 'oklch(0.4 0.1 60)', lineHeight: 1.5 }}>
                        มีประวัติถอนตัว/ค้างจ่าย — ควรพิจารณาก่อนเล่นวงเดียวกัน
                      </div>
                    )}
                  </>
                ) : <div style={{ padding: 20, textAlign: 'center', color: PS.textMuted, fontSize: 13 }}>กำลังโหลดประวัติ…</div>}

                <button data-testid="open-concern-btn" onClick={() => setMode('concern')} className="ps-press" style={{ width: '100%', height: 46, marginTop: 16, background: 'transparent', border: `1px solid ${PS.line}`, borderRadius: 12, fontSize: 13.5, fontWeight: 600, fontFamily: PS.fontUI, color: PS.text, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <Icon.shield size={15} stroke={PS.ink} /> แจ้งข้อสงสัย (เก็บเป็นหลักฐานส่วนตัว)
                </button>
              </div>
            ) : (
              <div className="ps-fade">
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>แจ้งข้อสงสัยเกี่ยวกับ {person.name}</div>
                <div style={{ fontSize: 11.5, color: PS.textMuted, marginBottom: 10, lineHeight: 1.5 }}>เก็บเป็นหลักฐานส่วนตัวของคุณเท่านั้น — คนอื่นในวงไม่เห็น ใช้อ้างอิงเมื่อมีข้อพิพาท</div>
                <textarea data-testid="concern-reason" value={reason} onChange={e => setReason(e.target.value)} placeholder="เช่น ท้าวขอเลื่อนวันโอนบ่อย / ไม่ยอมเปิดรายชื่อสมาชิก…" rows={4}
                  style={{ width: '100%', borderRadius: 12, border: `1px solid ${PS.line}`, padding: 12, fontSize: 13.5, fontFamily: PS.fontUI, background: PS.surface, outline: 'none', resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                  <button data-testid="concern-cancel-btn" onClick={() => setMode('info')} className="ps-press" style={{ flex: '0 0 auto', height: 48, padding: '0 18px', background: 'transparent', border: `1px solid ${PS.line}`, borderRadius: 12, fontSize: 13.5, fontWeight: 600, fontFamily: PS.fontUI, color: PS.text }}>ย้อนกลับ</button>
                  <button data-testid="concern-submit-btn" disabled={busy || !reason.trim()} onClick={submit} className="ps-press" style={{ flex: 1, height: 48, background: PS.ink, color: PS.page, border: 'none', borderRadius: 12, fontSize: 14, fontWeight: 600, fontFamily: PS.fontUI, opacity: (busy || !reason.trim()) ? 0.5 : 1 }}>{busy ? 'กำลังบันทึก…' : 'บันทึกเป็นหลักฐาน'}</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
